import { Button, Space, Table, Typography, Image, Skeleton, Tooltip } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { compose } from "redux";
import { connect, useSelector } from "react-redux";
import { createStructuredSelector } from "reselect";
import { useParams, withRouter } from "react-router";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import _ from "lodash";
import { useTranslation, withTranslation } from "react-i18next";

import {
  getNotificationDataAPI,
  getTopOpportunityPatientsAPI,
  exportListAPI,
  parseSearchFormParams
} from "./../api/api";
import { Routes } from "../constant/routes";
import { dateFormatter, dateTimeFormatter } from "../utils/date";
import { PAGE_SIZE, selectedViews } from "../constant/table";
import { EditColumn, PatientNameColumn, StarColumn } from "./TableSection/CommonColumns";
import { GRAY_1600, GRAY_400, GRAY_500, GRAY_700, WHITE } from "../constant/colors";
import ErrorMessage from "./customComponent/customMessages/ErrorMessage";
import SuccessMessage from "./customComponent/customMessages/SuccessMessage";
import ExportButton from "./ExportButton/ExportButton";
import { handleExportResponse } from "../utils/export";
import { MAX_EXPORT_RECORDS, LIST_TYPES } from "../constant/export";

import { selectTableColumns, selectRelevantPatientData, selectedViewTableData } from "../store/selector";
import styled from "styled-components";
import { patientInsuranceCell } from "./TableCells";
import { ColumnSelector } from "../component/ColumnSelector";
import ACTIONS from "../store/action";
import { SUPPORTED_COVERAGES } from "../constant/patient";
import PatientPageCount from "../component/TableSection/PatientPageCount";
import { ColumnSelectorContainer } from "../component/TableSection/TableStyle";
import { commonBulkActionsTableStyle } from "../component/TableSection/BulkActionsTableStyle";
import useColumnResizeAndReorder from "../hooks/useColumnResizeAndReorder";
import useTableInlineEdit from "../hooks/InlineEdit/useTableInlineEdit";
import { TableColumnWrapper } from "@tailormed/common-client/component/ColumnResizeAndReorder/TableColumnWrapper";
import { ColumnsKeys } from "../constant/columns";
import { BulkActionsContainer } from "../component/BulkActions";
import moment from "moment-timezone";
import { useGetSetting } from "../hooks/getSetting";
import { fontWeights, sizes } from "../constant/styles";
import { ASCEND, DECEND, desiredOrderOfStatuses, SORT_DIRECTIONS_DEFAULT } from "../constant/sort";
import { getSortingChanges, sortListByOrder } from "../utils/sort";
import { TitledTooltip } from "./TitledTooltip";
import TableFilters from "./TableFilters";
import {
  getRelevantPatientsTableConfigFromLocalStorage,
  OOP_MET_STATUS,
  saveRelevantPatientsTableConfigToLocalStorage
} from "../constant/relevantPatients";
import ArrowBack from "../assets/svg/ArrowBack";
import EmptyTableMessage from "./TableSection/EmptyTableMessage";
import { scrollTableToTop } from "../utils/fetchTableData";
import LinkableCell from "./UI/LinkableCell";
import { handleRowNavigation } from "../utils/tableRowNavigation";
import { capitalizeFirstLetter } from "../utils/string";

const { Text } = Typography;

const StyledTable = styled(Table)`
  ${(props) => commonBulkActionsTableStyle(false, props.$hasSelection)}
  margin-top: 0px;

  .ant-table {
    max-height: ${(props) => {
      if (props.filtersVisible) {
        return "calc(100vh - 290px)";
      }
      return "calc(100vh - 310px)";
    }};
  }

  .ant-table-empty {
    .ant-table-tbody {
      height: calc(100vh - 350px);
    }
  }
`;

const ContainerStyle = styled.div`
  margin: 0px 15px 0px 15px;
  position: relative;
  background: ${WHITE};
  border-radius: 10px;
  padding: 10px 15px 0px 15px;
`;

const HeaderStyle = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
`;

const BackButtonStyle = styled(Button)`
  background-color: transparent;
  border: none;
  padding: 0px;
`;

const AssistanceProgramNameStyle = styled(Text)`
  display: block;
  font-size: ${sizes.medium_xlarge};
  font-weight: ${fontWeights.semibold};
  color: ${GRAY_1600};
`;

const AssistanceProgramStatusStyle = styled(Text)`
  display: block;
  font-size: ${sizes.medium};
  font-weight: ${fontWeights.semibold};
  color: ${GRAY_500};
`;

const ImageStyle = styled(Image)`
  margin: 12px 20px 12px 20px;
  width: 142px;
`;

const ContentStyle = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  transition: all 0.3s ease;
  margin-top: 10px;
`;

const TopOpportunitiesComponent = ({
  t,
  history,
  setSelectedView,
  tableColumns,
  setTableDataAct,
  tableData,
  clearSelectedView
}) => {
  const relevantPatientsTableConfig = getRelevantPatientsTableConfigFromLocalStorage();
  const relevantPatientsSorter = relevantPatientsTableConfig?.sorter || {};
  const relevantPatientsFilters = relevantPatientsTableConfig?.filters || {};
  const relevantPatientsCurrentPage = relevantPatientsTableConfig?.currentPage || {};
  const relevantPatientsId = relevantPatientsTableConfig?.id && parseInt(relevantPatientsTableConfig.id);

  const [isFetching, setIsFetching] = useState([]);
  const [hoveringLineIndex, setHoveringLineIndex] = useState(null);
  const { processColumns, components, containerRef, reorderGuideLeft, tableProps } = useColumnResizeAndReorder(
    ColumnsKeys.tables["top-opportunities"],
    ["star", "edit"]
  );
  const [filtersVisible, setFiltersVisible] = useState(false);
  const { notificationId } = useParams();
  const [notification, setNotification] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  const totalRecordsCount = tableData?.totalPatients;
  const sortingEnabled = tableData?.sortingEnabled;

  const [statuses] = useGetSetting(["statuses"]);
  // statuses is "" until the settings load; sortListByOrder returns [] for empty input.
  const filteredStatuses = useMemo(() => sortListByOrder(statuses, desiredOrderOfStatuses, "value"), [statuses]);
  const filteredTableDate = tableData?.patients?.filter(({ status }) => status != OOP_MET_STATUS) || [];

  const visibleRecordIds = useMemo(
    () => filteredTableDate.map((patient) => patient.patientId).filter(Boolean),
    [filteredTableDate]
  );

  const bulkAdditionalFilters = useMemo(() => {
    const apId = notification?.assistanceProgram?.id;
    if (!apId) return undefined;
    return { multiples: { assistancePrograms: [{ value: apId }] } };
  }, [notification]);

  useEffect(() => {
    setSelectedView(selectedViews.topOpportunity);
  }, []);

  useEffect(() => {
    if (
      tableData?.patients?.length === 0 &&
      tableData?.totalPatients > 0 &&
      tableData?.totalPatients % PAGE_SIZE === 0 &&
      relevantPatientsCurrentPage > 1
    ) {
      // Persist the stepped-back page: relevantPatientsCurrentPage is re-read from localStorage
      // on every render, so without saving it here each empty result re-fetches the same page
      // forever (infinite update loop → React #185, e.g. when totalPatients dropped to 0).
      saveRelevantPatientsTableConfigToLocalStorage({
        ...relevantPatientsTableConfig,
        currentPage: relevantPatientsCurrentPage - 1
      });
      fetchTopOpportunityPatients(null, null, relevantPatientsSorter, { current: relevantPatientsCurrentPage - 1 });
    }
  }, [tableData]);

  useEffect(() => {
    const fetchData = async () => {
      const notificationDate = await fetchNotificationData();

      if (notificationDate) {
        const assistanceProgramId = notificationDate.assistanceProgram.id;
        //Note: Reset the page number to 1 when navigating to a different relevant patient
        const shouldResetPage = relevantPatientsId !== 0 && relevantPatientsId !== parseInt(notificationId);

        const newFilters = {
          ...relevantPatientsFilters,
          assistancePrograms: [{ value: assistanceProgramId }]
        };

        const currentPage = shouldResetPage ? 1 : relevantPatientsCurrentPage;

        saveRelevantPatientsTableConfigToLocalStorage({
          ...relevantPatientsTableConfig,
          currentPage,
          filters: newFilters
        });

        await fetchTopOpportunityPatients(assistanceProgramId, null, relevantPatientsSorter, {
          current: currentPage
        });
      }
    };

    fetchData();
  }, [notificationId]);

  const fetchNotificationData = async () => {
    let notificationData = null;
    const { state } = history.location || {};
    const { notification, id } = state || {};

    if (notification && id) {
      notificationData = history.location.state.notification;
    } else {
      try {
        setIsFetching(true);
        const notificationDataRes = await getNotificationDataAPI(notificationId);
        if (notificationDataRes && notificationDataRes.data) {
          notificationData = notificationDataRes.data;
        } else {
          ErrorMessage(t("top_opportunities.failed_to_get_notification_data"));
        }
      } catch (error) {
        ErrorMessage(t("top_opportunities.failed_to_get_notification_data"));
      } finally {
        setIsFetching(false);
      }
    }

    if (notificationData) {
      setNotification(notificationData);
    }

    return notificationData;
  };

  const fetchData = (pagination, filters, sorter) => {
    const { current: nextPage } = pagination;

    if (nextPage === relevantPatientsCurrentPage) {
      const { columnIsChanged, sortDirectionIsChanged, nextColumnName, nextSortBy } = getSortingChanges(
        relevantPatientsSorter,
        sorter
      );

      if (columnIsChanged || sortDirectionIsChanged) {
        saveRelevantPatientsTableConfigToLocalStorage({
          ...relevantPatientsTableConfig,
          sorter: { ...sorter, columnKey: nextColumnName, order: nextSortBy },
          currentPage: relevantPatientsCurrentPage === 1 ? pagination.current : 1
        });
        fetchTopOpportunityPatients(
          null,
          null,
          sorter,
          relevantPatientsCurrentPage === 1 ? pagination : { current: 1 }
        );
      }
    } else {
      fetchTopOpportunityPatients(null, null, sorter, pagination);
    }
    scrollTableToTop();
  };

  const fetchTopOpportunityPatients = async (
    assistanceProgramId,
    filters,
    sorter = relevantPatientsSorter,
    pagination
  ) => {
    const finalSorter = sorter.column ? sorter : {};
    const parsedFilters = filters && _.omitBy(filters, _.isNull);
    const filtersForAPIRequest = parsedFilters
      ? parsedFilters
      : {
          ..._.omitBy(relevantPatientsFilters, _.isNull),
          assistancePrograms: [
            { value: assistanceProgramId ? assistanceProgramId : notification?.assistanceProgram.id }
          ]
        };
    setIsFetching(true);
    try {
      const parsedFilters = {
        multiples: filtersForAPIRequest,
        isUnassigned: filtersForAPIRequest.isUnassigned,
        isNoFacility: filtersForAPIRequest.isNoFacility
      };

      const topOpportunityPatientsRes = await getTopOpportunityPatientsAPI(
        parsedFilters,
        finalSorter,
        pagination?.current
      );
      if (topOpportunityPatientsRes && topOpportunityPatientsRes.data && topOpportunityPatientsRes.data.patients) {
        setTableDataAct(topOpportunityPatientsRes.data, finalSorter);
      } else {
        ErrorMessage(t("errorMessages.top_opportunity_patients"));
      }
    } catch (error) {
      ErrorMessage(t("errorMessages.top_opportunity_patients"));
    } finally {
      setIsFetching(false);
    }
  };

  const renderWithLink = ({ patientId, journeyId }, renderFn) => (
    <LinkableCell to={Routes.ROOT + `patient/${patientId}/journey/${journeyId}`}>{renderFn()}</LinkableCell>
  );

  const { renderPatientCell } = useTableInlineEdit({
    t,
    view: selectedViews.topOpportunity,
    renderWithLink,
    // With a status filter active, an inline edit can make a row no longer match — re-fetch so it
    // drops out of the list (the optimistic patch updates the value but can't remove the row).
    onAfterCommit: () => {
      if (filteredStatuses.length > 0) {
        // Re-fetch the same page the user is on (not -1 — that sent current:0 → skip:-25 → SQL error
        // on page 1). If the page ends up empty, the tableData effect above steps back a page.
        fetchTopOpportunityPatients(null, null, relevantPatientsSorter, {
          current: relevantPatientsCurrentPage
        });
      }
    }
  });

  const handleFetchFilter = (filters) => {
    fetchTopOpportunityPatients(null, filters);
  };

  const handleExport = async (fileType) => {
    setIsExporting(true);
    try {
      const visibleColumns = Object.keys(tableColumns[selectedViews.topOpportunity] || {}).filter(
        (key) => tableColumns[selectedViews.topOpportunity][key]?.display
      );

      // Transform filters from UI format to server format using parseSearchFormParams
      const filtersWithAssistanceProgram = {
        ...relevantPatientsFilters,
        multiples: {
          ...relevantPatientsFilters?.multiples,
          assistancePrograms: notification?.assistanceProgram?.id ? [{ value: notification.assistanceProgram.id }] : []
        }
      };

      const transformedFilters = parseSearchFormParams({
        selectedView: selectedViews.topOpportunity,
        page: 1,
        limit: MAX_EXPORT_RECORDS,
        filters: filtersWithAssistanceProgram
      });

      const response = await exportListAPI({
        listType: LIST_TYPES.PATIENTS,
        selectedView: selectedViews.topOpportunity,
        filters: transformedFilters.filters || {},
        sorting: relevantPatientsSorter || {},
        visibleColumns,
        fileType
      });

      handleExportResponse(response);
      SuccessMessage(t("export.downloadStarted"));
    } catch (error) {
      ErrorMessage(t("export.error_message"));
    } finally {
      setIsExporting(false);
    }
  };

  const withSorting = (sortingEnabled, tableColumn, { sorterTooltipText, ...rest }, sortDirection) => {
    const [threshold] = useGetSetting(["dashboard.sorting-threshold"]);
    const { t } = useTranslation();
    const selectedViewData = useSelector(selectedViewTableData);
    const { sorter } = selectedViewData;
    const sortingTooltipTitle = threshold && t("sortInfo", { threshold: threshold / 1000 });

    const sortingActionTooltip =
      sorterTooltipText ||
      (() => {
        if (sorter?.column?.key === tableColumn.key) {
          if (sorter?.order === DECEND) {
            return t("columns.click_to_cancel_sorting");
          }
          if (sorter?.order === ASCEND) {
            return t("columns.click_to_sort_descending");
          }
        }
        return t("columns.click_to_sort_ascending");
      })();

    return {
      sorter: sortingEnabled,
      sortDirections: sortDirection ? sortDirection : SORT_DIRECTIONS_DEFAULT,
      ...tableColumn,
      title: () => (
        <div style={{ display: "flex", gap: "5px" }} id={`columnـ${tableColumn.dataIndex}`}>
          <Tooltip title={sortingActionTooltip}>
            <Text style={{ color: GRAY_700 }}>{tableColumn.title}</Text>
          </Tooltip>
          <div>
            {!sortingEnabled && (
              <TitledTooltip text={sortingTooltipTitle}>
                <ExclamationCircleOutlined style={{ fontSize: "12px", alignSelf: "center", color: GRAY_400 }} />
              </TitledTooltip>
            )}
          </div>
        </div>
      ),
      showSorterTooltip: false,
      ...rest,
      sortOrder: sorter?.column && sorter?.column?.key === tableColumn.key && sorter?.order
    };
  };

  const columns = [
    {
      dataIndex: tableColumns[selectedViews.topOpportunity]?.star?.dataIndex,
      key: tableColumns[selectedViews.topOpportunity]?.star?.key,
      width: 35,
      render: (text, record) => <StarColumn record={record} />
    },
    withSorting(sortingEnabled, tableColumns[selectedViews.topOpportunity].name_id, {
      sorterTooltipText: t("columns.sortById"),
      render: (text, record) => renderWithLink(record, () => <PatientNameColumn record={record} />)
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.topOpportunity]?.isDeceased, {
      render: (_, record) => renderPatientCell("isDeceased", record)
    }),
    {
      ...tableColumns[selectedViews.topOpportunity]?.phone_number,
      render: (text, record) => renderPatientCell("phone_number", record)
    },
    {
      ...tableColumns[selectedViews.topOpportunity]?.dob,
      render: (text, record) => renderPatientCell("dob", record)
    },
    {
      ...tableColumns[selectedViews.topOpportunity]?.ssn,
      render: (text, record) => renderPatientCell("ssn", record)
    },
    {
      ...tableColumns[selectedViews.topOpportunity]?.primaryDiagnosis,
      dataIndex: "diagnosises",
      render: (text, record) => renderWithLink(record, () => (record.diagnosises[0] ? record.diagnosises[0] : null))
    },
    {
      ...tableColumns[selectedViews.topOpportunity]?.assignee,
      render: (text, record) => renderPatientCell("assignee", record)
    },
    {
      ...tableColumns[selectedViews.topOpportunity]?.facility,
      sorter: false,
      render: (_, record) => renderPatientCell("facility", record)
    },
    withSorting(sortingEnabled, tableColumns[selectedViews.topOpportunity]?.physician, {
      render: (_, record) => renderPatientCell("physician", record)
    }),

    {
      ...tableColumns[selectedViews.topOpportunity]?.primaryInsurancePayer,
      render: (text, record) =>
        renderWithLink(record, () => patientInsuranceCell(SUPPORTED_COVERAGES.primary)(text, record))
    },
    {
      ...tableColumns[selectedViews.topOpportunity]?.secondaryInsurancePayer,
      render: (text, record) =>
        renderWithLink(record, () => patientInsuranceCell(SUPPORTED_COVERAGES.secondary)(text, record))
    },
    {
      ...tableColumns[selectedViews.topOpportunity]?.tertiaryInsurancePayer,
      render: (text, record) =>
        renderWithLink(record, () => patientInsuranceCell(SUPPORTED_COVERAGES.tertiary)(text, record))
    },
    withSorting(sortingEnabled, tableColumns[selectedViews.topOpportunity]?.applicationStatus, {
      render: (text, record) => renderWithLink(record, () => text)
    }),
    {
      ...tableColumns[selectedViews.topOpportunity]?.agreementSignatureDate,
      render: (text, record) =>
        renderWithLink(record, () => record.activeConsentDate && dateFormatter(record.activeConsentDate))
    },
    withSorting(sortingEnabled, tableColumns[selectedViews.topOpportunity]?.patientStatus, {
      render: (_, record) => renderPatientCell("patientStatus", record)
    }),

    withSorting(sortingEnabled, tableColumns[selectedViews.topOpportunity]?.followupDate, {
      render: (_, record) => renderPatientCell("followupDate", record)
    }),
    {
      dataIndex: tableColumns[selectedViews.topOpportunity]?.edit?.dataIndex,
      key: tableColumns[selectedViews.topOpportunity]?.edit?.key,
      render: (_, record, rowIndex) => <EditColumn isHovering={hoveringLineIndex === rowIndex} record={record} />
    },
    withSorting(sortingEnabled, tableColumns[selectedViews.topOpportunity]?.creationDate, {
      render: (text, record) => renderWithLink(record, () => moment(record.creationDate).format("MMM D, YYYY"))
    })
  ];

  return (
    <ContainerStyle>
      <HeaderStyle>
        <Space direction="vertical" size={0}>
          <Tooltip title={t("back")}>
            <BackButtonStyle
              onClick={() => {
                clearSelectedView();
                history.push(Routes.ROOT);
              }}
              type="text"
            >
              <ArrowBack />
            </BackButtonStyle>
          </Tooltip>

          <AssistanceProgramNameStyle>
            {notification &&
              t("top_opportunities.relevant_patients_for_assistance_program_name", {
                assistanceProgramName: notification.assistanceProgram.name
              })}
          </AssistanceProgramNameStyle>

          <AssistanceProgramStatusStyle>
            {notification &&
              notification.assistanceProgram.status &&
              t("top_opportunities.program_status_time", {
                status: capitalizeFirstLetter(
                  t(`patientPotentialSavings.${notification.assistanceProgram.status.replace(" ", "_")}`)
                ),
                time: dateTimeFormatter(notification.createdAt, "MM/DD/YYYY hh:mm A")
              })}
          </AssistanceProgramStatusStyle>
        </Space>

        <Space
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
          }}
        >
          <ImageStyle
            preview={false}
            src={
              notification && notification.assistanceProgram && notification.assistanceProgram.logo ? (
                notification.assistanceProgram.logo
              ) : (
                <Skeleton.Image />
              )
            }
          />
        </Space>

        <Space
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            flex: 1
          }}
        >
          <ExportButton recordCount={totalRecordsCount || 0} onExport={handleExport} isLoading={isExporting} />
        </Space>
      </HeaderStyle>

      <ContentStyle filtersVisible={filtersVisible}>
        <div
          style={{
            flex: "0 0 288px",
            maxWidth: filtersVisible ? "288px" : "0px",
            transition: "max-width 0.3s ease"
          }}
        >
          <TableFilters
            filtersVisible={filtersVisible}
            setFiltersVisible={setFiltersVisible}
            handleFetchRelevantPatientFilter={handleFetchFilter}
            showSaveNewListBtn={false}
          />
        </div>

        <div style={{ flex: "1 1 auto", overflow: "hidden" }}>
          <BulkActionsContainer
            viewKey={selectedViews.topOpportunity}
            entityType="patient"
            visibleRecordIds={visibleRecordIds}
            totalRecords={totalRecordsCount || 0}
            rowKey="patientId"
            additionalFilters={bulkAdditionalFilters}
          >
            {({ rowSelection }) => (
              <>
                {!!filteredTableDate.length && (
                  <ColumnSelectorContainer isRelevantPatientView={true}>
                    <ColumnSelector />
                  </ColumnSelectorContainer>
                )}

                <TableColumnWrapper containerRef={containerRef} reorderGuideLeft={reorderGuideLeft}>
                  <StyledTable
                    {...tableProps}
                    size="small"
                    loading={isFetching}
                    rowClassName="cursor-pointer"
                    style={{ width: "100%" }}
                    filtersVisible={filtersVisible}
                    components={components}
                    onRow={(record, rowIndex) => {
                      return {
                        onMouseEnter: () => setHoveringLineIndex(rowIndex),
                        onMouseLeave: () => setHoveringLineIndex(null),
                        onClick: (event) =>
                          handleRowNavigation(
                            event,
                            `${Routes.ROOT}patient/${record.patientId}/journey/${record.journeyId}`,
                            history
                          )
                      };
                    }}
                    columns={processColumns(
                      columns.filter((col) => tableColumns[selectedViews.topOpportunity]?.[col.key]?.display)
                    )}
                    locale={{
                      emptyText: <EmptyTableMessage type="relevantPatients" />
                    }}
                    onChange={fetchData}
                    rowKey="patientId"
                    rowSelection={rowSelection}
                    $hasSelection={rowSelection?.selectedRowKeys?.length > 0}
                    pagination={{
                      size: "small",
                      showSizeChanger: false,
                      current: relevantPatientsCurrentPage,
                      defaultPageSize: PAGE_SIZE,
                      pageSize: PAGE_SIZE,
                      position: ["bottom"],
                      total: totalRecordsCount,
                      onChange: (pagination) =>
                        saveRelevantPatientsTableConfigToLocalStorage({
                          ...relevantPatientsTableConfig,
                          currentPage: pagination,
                          id: notificationId
                        }),
                      showTotal: (total, range) => (
                        <div style={{ display: "flex", flexDirection: "row" }}>
                          <PatientPageCount total={total} range={range} isAccurateCount={tableData.isAccurateCount} />
                        </div>
                      )
                    }}
                    dataSource={
                      tableData && tableData.patients
                        ? filteredTableDate
                            .map((patient) => ({
                              ...patient,
                              key: patient.patientId
                            }))
                            .sort((a, b) => b.isActive - a.isActive)
                        : null
                    }
                  />
                </TableColumnWrapper>
              </>
            )}
          </BulkActionsContainer>
        </div>
      </ContentStyle>
    </ContainerStyle>
  );
};

const mapStateToProps = createStructuredSelector({
  tableColumns: selectTableColumns,
  tableData: selectRelevantPatientData
});

const mapDispatchToProps = (dispatch) => ({
  setSelectedView: (selectedView) => dispatch(ACTIONS.setSelectedView(selectedView)),
  setTableDataAct: (tableData, sorter, selectedPage) =>
    dispatch(ACTIONS.setTablesData(tableData, selectedPage, selectedViews.topOpportunity, sorter)),
  clearSelectedView: () => dispatch(ACTIONS.clearSelectedView())
});

const TopOpportunities = compose(
  withRouter,
  withTranslation(),
  connect(mapStateToProps, mapDispatchToProps)
)(TopOpportunitiesComponent);

export { TopOpportunities };
